import { makeStyles, Typography } from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderLog } from 'interfaces';

/**
 * Countdown timer that anchors at `log.acceptedAt` and ticks down from
 * `log.acceptPrepTimeSeconds`. While the order is in 'accepted' status
 * the timer is live (1s tick). Once the order advances to
 * 'ready_for_pickup' or later, the timer freezes at the elapsed time
 * recorded by `log.lastStatusCallAt - log.acceptedAt`, so a refresh
 * after mark-ready keeps showing the same final value.
 *
 * Display rules:
 * - Positive (still in prep window): green, "MM:SS left"
 * - Negative (overdue): red, "MM:SS late"
 * - Final freeze on ready_for_pickup: shows what's left at that moment,
 *   prefixed with "READY:" so staff can see if they made the deadline.
 */

const useStyles = makeStyles((theme) => ({
  wrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    fontWeight: 700,
    fontSize: '0.95rem',
    fontFamily: 'monospace',
  },
  positive: {
    background: '#e8f5e9',
    color: '#1b5e20',
  },
  negative: {
    background: '#ffebee',
    color: '#b71c1c',
  },
  finished: {
    background: '#e3f2fd',
    color: '#0d47a1',
  },
  label: {
    fontWeight: 400,
    fontSize: '0.7rem',
    opacity: 0.7,
  },
}));

interface Props {
  log: ExternalOrderLog;
}

const formatMMSS = (totalSeconds: number): string => {
  const sign = totalSeconds < 0 ? '-' : '';
  const abs = Math.abs(Math.floor(totalSeconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Courier buffer (minutes) added to prep time to decide when the kitchen
 * starts a pre-order. Mirrors the API's
 * ExternalOrderInjector::PREORDER_COURIER_BUFFER_MINUTES default; if that
 * is retuned via EXTERNAL_PREORDER_BUFFER_MINUTES, this display estimate
 * drifts by the same amount (times shown are indicative, the API decides).
 */
const COURIER_BUFFER_MINUTES = 10;

const hhmm = (d: Date) =>
  `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;

/** due_datetime out of parsed_payload — inner keys are not camelised. */
export const getDueDate = (log: ExternalOrderLog): Date | null => {
  const pp: any = log.parsedPayload ?? {};
  const raw = pp.due_datetime ?? pp.dueDatetime ?? null;
  if (!raw) return null;
  const d = new Date(raw as string);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * A pre-order is one whose due time is far enough out that the kitchen
 * deliberately waits before cooking. Returns the moment the kitchen
 * starts, or null for ASAP orders.
 */
export const getKitchenStart = (log: ExternalOrderLog): Date | null => {
  const due = getDueDate(log);
  if (!due) return null;
  const prepSeconds = log.acceptPrepTimeSeconds ?? 25 * 60;
  const leadMs = (prepSeconds + COURIER_BUFFER_MINUTES * 60) * 1000;
  const start = new Date(due.getTime() - leadMs);
  const acceptedRaw = log.acceptedAt;
  const anchor = acceptedRaw ? new Date(acceptedRaw as string) : new Date();
  // If the kitchen would start at/before acceptance, it's just an ASAP order.
  return start.getTime() > anchor.getTime() + 60 * 1000 ? start : null;
};

export const PrepTimer: React.FC<Props> = ({ log }) => {
  const classes = useStyles();
  const [, forceRerender] = React.useState(0);

  const status = log.boltOrderStatus;
  const acceptedAtRaw = log.acceptedAt;
  const prepSeconds = log.acceptPrepTimeSeconds ?? 0;

  // Re-render every second while the timer is live (only when in accepted
  // state). Cleared as soon as the status moves on or the component
  // unmounts.
  React.useEffect(() => {
    if (status !== 'accepted' || !acceptedAtRaw) {
      return;
    }
    const interval = setInterval(() => forceRerender((x) => x + 1), 1000);
    return () => clearInterval(interval);
  }, [status, acceptedAtRaw]);

  if (!acceptedAtRaw || !prepSeconds) {
    return null;
  }

  const acceptedAt = new Date(acceptedAtRaw as string);
  if (isNaN(acceptedAt.getTime())) {
    return null;
  }

  const isFrozen =
    status === 'ready_for_pickup' ||
    status === 'picked_up' ||
    status === 'delivered';

  const referenceTime = isFrozen && log.lastStatusCallAt
    ? new Date(log.lastStatusCallAt as string)
    : new Date();

  // PRE-ORDER: the deadline is the customer's due time, NOT accept+prep.
  // Counting from accept would scream "late" for hours on an order that
  // is not due until this evening (seen live 2026-07-15 on order 2262).
  const due = getDueDate(log);
  const kitchenStart = getKitchenStart(log);
  if (due && kitchenStart) {
    const cookingStarted = referenceTime.getTime() >= kitchenStart.getTime();
    if (!cookingStarted) {
      return (
        <div className={`${classes.wrap} ${classes.finished}`}>
          <span>{hhmm(due)}</span>
          <span className={classes.label}>
            pre-order · kitchen {hhmm(kitchenStart)}
          </span>
        </div>
      );
    }
    const secondsToDue = (due.getTime() - referenceTime.getTime()) / 1000;
    const lateForDue = secondsToDue < 0;
    return (
      <div
        className={`${classes.wrap} ${
          isFrozen
            ? classes.finished
            : lateForDue
            ? classes.negative
            : classes.positive
        }`}
      >
        <span>{formatMMSS(secondsToDue)}</span>
        <span className={classes.label}>
          {isFrozen
            ? `ready · due ${hhmm(due)}`
            : lateForDue
            ? `past due ${hhmm(due)}`
            : `until due ${hhmm(due)}`}
        </span>
      </div>
    );
  }

  const elapsedSeconds = (referenceTime.getTime() - acceptedAt.getTime()) / 1000;
  const remainingSeconds = prepSeconds - elapsedSeconds;
  const isLate = remainingSeconds < 0;

  let cls = classes.positive;
  let label = 'time left';
  let value = formatMMSS(remainingSeconds);

  if (isFrozen) {
    cls = classes.finished;
    label = isLate ? 'finished late' : 'finished on time';
    value = formatMMSS(Math.abs(remainingSeconds));
  } else if (isLate) {
    cls = classes.negative;
    label = 'late';
    value = formatMMSS(remainingSeconds);
  }

  return (
    <div className={`${classes.wrap} ${cls}`}>
      <span>{value}</span>
      <span className={classes.label}>{label}</span>
    </div>
  );
};
